import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { PulseLoader } from 'react-spinners';

import { IUser, bScreenMode, userState } from '../atoms';
import SiteDashboard from '../components/SiteDashboard';
import { registerSignin } from '../services/register';
import Cookies from 'js-cookie';
import i18n from '../translation/i18n';
import { COMPANY_DASHBOARD, SITE_DASHBOARD, TAREA_DASHBOARD } from '../_constants';
import { apiGet } from '../services/_common';
import NotFound from './NotFound';

const Dashboard = () => {
  const setUserInfo = useSetRecoilState<IUser>(userState);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const setColorMode = useSetRecoilState(bScreenMode);
  const [accessToken, setAccessToken] = useState();
  const queryParamHCd = queryParams.get('hCd');
  const queryParamSCd = queryParams.get('sCd');
  const queryParamTatCd = queryParams.get('areaCd');
  const queryParamCCd = queryParams.get('cCd');

  const loginRetryTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if ((queryParamHCd && queryParamSCd) || queryParamCCd) {
      loginAPI();
    }
    return () => clearTimeout(loginRetryTimer.current);
  }, []);

  /**
   * 수정자 : 홍선영
   * 수정일자 : 2026.09.22
   * 문제 : 서버 장애 중 로그인이 실패하면(연결 끊김·5xx) 재시도 없이 빈 화면으로 멈춤
   * 수정내용 : 로그인 실패 시 간격을 늘려가며(5초 → 최대 60초) 재시도
   */
  const loginAPI = async (retryDelayMs = 5000) => {
    const res = await registerSignin({ id: 'siteDashboardViewer', password: 'test', passwordErrorCount: 0 });
    const { data, statusCode } = res?.data ?? {};
    if (statusCode === 200) {
      const { userId } = data.userInfo;
      const { userMenuList } = data;
      data?.accessToken && Cookies.set('prosafeToken', data.accessToken);
      data?.refreshToken && Cookies.set('refreshToken', data.refreshToken);
      Cookies.set('dashboardViewer', 'Y');
      setAccessToken(data.accessToken);
      setUserInfo((prev) => ({
        ...prev,
        ...data.userInfo,
        userId,
        userMenuList,
      }));

      if (queryParamHCd && queryParamSCd) getSiteInfoAPI(queryParamHCd, queryParamSCd);
      if (queryParamCCd) getCompanySetAPI(queryParamCCd);
    } else {
      loginRetryTimer.current = setTimeout(() => loginAPI(Math.min(retryDelayMs * 2, 60000)), retryDelayMs);
    }
  };

  const isDataNotEmpty = (data: object | null | undefined): boolean => !!(data && Object.keys(data).length);

  // 사내 설정 정보 조회 API
  const getCompanySetAPI = async (cCd: string) => {
    const req = { cCd };
    const res = await apiGet({ path: '/company/set', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      if (isDataNotEmpty(data)) setColorMode(data.companySet.bScreenMode);
      else setColorMode('1');

      setUserInfo((prev) => ({ ...prev, hCd: cCd }));
    }
  };

  // 현장 정보 조회 API
  const getSiteInfoAPI = async (hCd: any, sCd: any) => {
    const req = { hCd, sCd };
    const res = await apiGet({ path: '/site/info', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      getSiteSetAPI(hCd, sCd, data);
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 현장 설정 정보 조회 API
  const getSiteSetAPI = async (hCd: string, sCd: string, siteInfo: any) => {
    const req = { hCd, sCd };
    const res = await apiGet({ path: '/setting/general', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200 && isDataNotEmpty(data)) {
      setUserInfo((prev) => ({
        ...prev,
        hCd,
        sCd,
        eExcelYn: siteInfo.eExcelYn,
        eListYn: siteInfo.eListYn,
        ePrintYn: siteInfo.ePrintYn,
        prejobtypeYn: data.generalSet.prejobtypeYn,
        elderlyAgeSet: data.generalSet.elderlyAgeSet,
        sessionTimeout: data.generalSet.sessionTimeout,
        eduPlace: data.generalSet.eduPlace,
        eduCharge: data.generalSet.eduCharge,
        eduStartTime: data.generalSet.eduStartTime,
        eduEndTime: data.generalSet.eduEndTime,
        fromHighpress: data.generalSet.fromHighpress,
        fromLowpress: data.generalSet.fromLowpress,
        toHighpress: data.generalSet.toHighpress,
        toLowpress: data.generalSet.toLowpress,
        accidentEdate: data.generalSet.accidentEdate,
        accidentSdate: data.generalSet.accidentSdate,
        accidentTday: data.generalSet.accidentTday,
      }));
      setColorMode(data.generalSet.bScreenMode);
      const lang = data.generalSet.bLangMode;
      switch (lang) {
        case '2':
          i18n.changeLanguage('en-US');
          break;
        case '3':
          i18n.changeLanguage('vi-VN');
          break;
        default:
          i18n.changeLanguage('ko-KR');
          break;
      }
    } else {
      // toast.error(t(ERROR));
    }
  };

  // 로그인 전(재시도 중 포함)에는 빈 화면 대신 로딩 표시
  const loginLoader = (
    <div className='centered-content'>
      <PulseLoader color='rgb(0, 122, 255)' size='10px' />
    </div>
  );

  if (queryParamHCd && queryParamSCd && queryParamTatCd)
    // 구역별 상황판
    return <div className='contents dashboard-monitor'>{accessToken ? <SiteDashboard isViewMode dGubun={TAREA_DASHBOARD} tatCd={queryParamTatCd} /> : loginLoader}</div>;
  if (queryParamHCd && queryParamSCd)
    // 현장용 상황판
    return <div className='contents dashboard-monitor'>{accessToken ? <SiteDashboard isViewMode dGubun={SITE_DASHBOARD} /> : loginLoader}</div>;
  if (queryParamCCd)
    // 사내용 상황판
    return <div className='contents dashboard-monitor company-dashboard'>{accessToken ? <SiteDashboard isViewMode dGubun={COMPANY_DASHBOARD} /> : loginLoader}</div>;
  return <NotFound />;
};

export default Dashboard;
